/**
 * =========================================================
 * Social Media Hub - Modern Color Palette
 * =========================================================
 *
 * A sleek, professional color scheme designed for a polished
 * social media management experience.
 */

const colors = {
  background: {
    default: "#f8fafc",
    paper: "#ffffff",
  },

  text: {
    main: "#64748b",
    focus: "#475569",
    primary: "#0f172a",
    secondary: "#64748b",
    disabled: "#94a3b8",
  },

  transparent: {
    main: "transparent",
  },

  white: {
    main: "#ffffff",
    focus: "#f8fafc",
  },

  black: {
    light: "#1e293b",
    main: "#0f172a",
    focus: "#020617",
  },

  primary: {
    main: "#6366f1",
    light: "#818cf8",
    dark: "#4f46e5",
    focus: "#4338ca",
    contrastText: "#ffffff",
  },

  secondary: {
    main: "#64748b",
    light: "#94a3b8",
    dark: "#475569",
    focus: "#334155",
  },

  info: {
    main: "#0ea5e9",
    light: "#38bdf8",
    dark: "#0284c7",
    focus: "#0369a1",
    contrastText: "#ffffff",
  },

  success: {
    main: "#10b981",
    light: "#34d399",
    dark: "#059669",
    focus: "#047857",
    contrastText: "#ffffff",
  },

  warning: {
    main: "#f59e0b",
    light: "#fbbf24",
    dark: "#d97706",
    focus: "#b45309",
    contrastText: "#ffffff",
  },

  error: {
    main: "#ef4444",
    light: "#f87171",
    dark: "#dc2626",
    focus: "#b91c1c",
    contrastText: "#ffffff",
  },

  light: {
    main: "#f1f5f9",
    focus: "#e2e8f0",
  },

  dark: {
    main: "#1e293b",
    focus: "#0f172a",
  },

  grey: {
    50: "#f8fafc",
    100: "#f1f5f9",
    200: "#e2e8f0",
    300: "#cbd5e1",
    400: "#94a3b8",
    500: "#64748b",
    600: "#475569",
    700: "#334155",
    800: "#1e293b",
    900: "#0f172a",
  },

  gradients: {
    primary: {
      main: "#818cf8",
      state: "#6366f1",
      deg: 135,
    },

    secondary: {
      main: "#94a3b8",
      state: "#64748b",
      deg: 135,
    },

    info: {
      main: "#38bdf8",
      state: "#0ea5e9",
      deg: 135,
    },

    success: {
      main: "#34d399",
      state: "#10b981",
      deg: 135,
    },

    warning: {
      main: "#fbbf24",
      state: "#f59e0b",
      deg: 135,
    },

    error: {
      main: "#f87171",
      state: "#ef4444",
      deg: 135,
    },

    dark: {
      main: "#334155",
      state: "#1e293b",
      deg: 135,
    },

    // Special gradients for cards and backgrounds
    card: {
      main: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
      colored: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
      success: "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
      warning: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)",
      error: "linear-gradient(135deg, #ef4444 0%, #f87171 100%)",
      info: "linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)",
    },

    // Background gradients
    background: {
      default: "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)",
      dark: "linear-gradient(180deg, #1e293b 0%, #0f172a 100%)",
    },
  },

  socialMediaColors: {
    facebook: {
      main: "#1877f2",
      dark: "#166fe5",
      light: "#e7f3ff",
    },

    twitter: {
      main: "#1da1f2",
      dark: "#1a91da",
      light: "#e8f5fe",
    },

    instagram: {
      main: "#e4405f",
      dark: "#d1304f",
      light: "#fce8ec",
    },

    linkedin: {
      main: "#0a66c2",
      dark: "#0958a8",
      light: "#e8f2fc",
    },

    threads: {
      main: "#000000",
      dark: "#000000",
      light: "#f5f5f5",
    },

    pinterest: {
      main: "#bd081c",
      dark: "#a50718",
      light: "#fce4e7",
    },

    youtube: {
      main: "#ff0000",
      dark: "#e60000",
      light: "#ffe6e6",
    },

    vimeo: {
      main: "#1ab7ea",
      dark: "#13a3d2",
      light: "#e5f7fc",
    },

    slack: {
      main: "#4a154b",
      dark: "#3d113e",
      light: "#f5eaf5",
    },

    dribbble: {
      main: "#ea4c89",
      dark: "#e73177",
      light: "#fceef4",
    },

    github: {
      main: "#333333",
      dark: "#24292e",
      light: "#f0f0f0",
    },

    reddit: {
      main: "#ff4500",
      dark: "#e03d00",
      light: "#fff0eb",
    },

    tumblr: {
      main: "#35465c",
      dark: "#2a3749",
      light: "#eef0f3",
    },
  },

  badgeColors: {
    primary: {
      background: "#e0e7ff",
      text: "#4338ca",
    },

    secondary: {
      background: "#f1f5f9",
      text: "#475569",
    },

    info: {
      background: "#e0f2fe",
      text: "#0369a1",
    },

    success: {
      background: "#d1fae5",
      text: "#047857",
    },

    warning: {
      background: "#fef3c7",
      text: "#b45309",
    },

    error: {
      background: "#fee2e2",
      text: "#b91c1c",
    },

    light: {
      background: "#f8fafc",
      text: "#64748b",
    },

    dark: {
      background: "#e2e8f0",
      text: "#1e293b",
    },
  },

  coloredShadows: {
    primary: "#6366f1",
    secondary: "#64748b",
    info: "#0ea5e9",
    success: "#10b981",
    warning: "#f59e0b",
    error: "#ef4444",
    light: "#94a3b8",
    dark: "#1e293b",
  },

  inputBorderColor: "#e2e8f0",

  tabs: {
    indicator: { boxShadow: "#e2e8f0" },
  },

  // Additional utility colors
  divider: "#e2e8f0",
  overlay: "rgba(15, 23, 42, 0.5)",
  tooltip: "#1e293b",
};

export default colors;
